import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fmtCurrency } from '../../lib/display-labels';

/**
 * A downhole quote's terms per tool (api 0488): "Minimum charge per run" and
 * "Lost in hole". Pre-filled from the rate card, editable while the quote is a
 * draft, and NOT part of the quote total — the panel says so. On accept these
 * values are what the customer's tool rates carry.
 *
 * An empty input means not quoted (null): the standard rate's then applies.
 */
export interface QuoteToolTerm {
  dh_rate_id: string;
  label: string;
  minimum_charge: string | null;
  lih_rate: string | null;
}

const input = 'w-28 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-2 py-1 text-right text-sm text-white focus:border-fieldiq-accent focus:outline-none';

function Row({ term, currency, editable, saving, onSave }: {
  term: QuoteToolTerm; currency: string; editable: boolean; saving: boolean;
  onSave: (t: QuoteToolTerm, minimumCharge: string, lihRate: string) => void;
}) {
  const [min, setMin] = useState(term.minimum_charge ?? '');
  const [lih, setLih] = useState(term.lih_rate ?? '');
  const dirty = min !== (term.minimum_charge ?? '') || lih !== (term.lih_rate ?? '');
  return (
    <tr className="border-t border-fieldiq-border">
      <td className="px-4 py-2 text-sm font-medium text-white">{term.label}</td>
      {editable ? (
        <>
          <td className="px-4 py-2 text-right">
            <input aria-label={`Minimum charge per run — ${term.label}`} className={input} inputMode="decimal"
              value={min} onChange={(e) => setMin(e.target.value)} placeholder="—" />
          </td>
          <td className="px-4 py-2 text-right">
            <input aria-label={`Lost in hole — ${term.label}`} className={input} inputMode="decimal"
              value={lih} onChange={(e) => setLih(e.target.value)} placeholder="—" />
          </td>
          <td className="px-4 py-2 text-right">
            <button type="button" disabled={!dirty || saving} onClick={() => onSave(term, min, lih)}
              className="fi-action fi-action--standard fi-action--secondary disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
            </button>
          </td>
        </>
      ) : (
        <>
          <td className="px-4 py-2 text-right text-sm text-white">{fmtCurrency(term.minimum_charge, currency)}</td>
          <td className="px-4 py-2 text-right text-sm text-white">{fmtCurrency(term.lih_rate, currency)}</td>
        </>
      )}
    </tr>
  );
}

export function QuoteToolTermsPanel({ terms, currency, editable, savingId, onSave }: {
  terms: QuoteToolTerm[]; currency: string; editable: boolean; savingId: string | null;
  onSave: (t: QuoteToolTerm, minimumCharge: string, lihRate: string) => void;
}) {
  if (terms.length === 0) return null;
  return (
    <section data-testid="quote-tool-terms"
      className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
      <div className="border-b border-fieldiq-border px-4 py-3">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Terms per tool</p>
        <h2 className="mt-1 text-base font-extrabold tracking-tight text-white">Minimum charge and lost in hole</h2>
        <p className="mt-1 text-xs text-fieldiq-muted">
          Charged only when they apply to a run. Not added to the quote total.
          {editable ? ' Leave a box empty to quote none; the standard rate then applies.' : ''}
        </p>
      </div>
      <table className="w-full">
        <thead>
          <tr className="text-left text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
            <th className="px-4 py-2">Tool</th>
            <th className="px-4 py-2 text-right">Minimum charge per run</th>
            <th className="px-4 py-2 text-right">Lost in hole</th>
            {editable && <th className="px-4 py-2" />}
          </tr>
        </thead>
        <tbody>
          {terms.map((t) => (
            <Row key={`${t.dh_rate_id}:${t.minimum_charge}:${t.lih_rate}`} term={t} currency={currency}
              editable={editable} saving={savingId === t.dh_rate_id} onSave={onSave} />
          ))}
        </tbody>
      </table>
    </section>
  );
}
